import { Lang } from '@yandex-int/i18n/lib/react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createBrowserRouter, Navigate, Route, RouterProvider, Routes, useNavigate, useParams } from 'react-router-dom';

import { ThemeProvider, ToasterComponent, ToasterProvider } from '@gravity-ui/uikit';
import { toaster } from '@gravity-ui/uikit/toaster-singleton';
import { AliceAgentAppPageContainer } from 'client/blocks/AliceAgentAppPage/AliceAgentAppPageContainer';
import { AliceAppsIndexPage } from 'client/blocks/AliceAppsIndexPage/AliceAppsIndexPage';
import { AliceExternalAppPageContainer } from 'client/blocks/AliceExternalAppPage/AliceExternalAppPageContainer';
import { AliceSkillModerationTestPage } from 'client/blocks/AliceSkillModerationTestPage/AliceSkillModerationTestPage';
import { InstallableAppPageContainer } from 'client/blocks/AppPage/InstallableAppPageContainer';
import { SkillAppPageContainer } from 'client/blocks/AppPage/SkillAppPageContainer';
import { AllAvailableAppsRouter } from 'client/blocks/AppsIndex/AllAvailableAppsRouter';
import AppsIndex from 'client/blocks/AppsIndex/AppsIndex';
import { AuthRedirect } from 'client/blocks/EmailPages/AuthRedirect';
import { EmailConfirmation } from 'client/blocks/EmailPages/EmailConfirmation';
import { Unsubscribe } from 'client/blocks/EmailPages/Unsubscribe';
import { ErrorBoundary } from 'client/blocks/ErrorBoundary/ErrorBoundary';
import { PageLoader } from 'client/blocks/Loader/PageLoader';
import { QuickApps } from 'client/blocks/quickApps/QuickApps';
import { useTwoFaToast } from 'client/blocks/TwoFaBanner/useTwoFaToast';
import { UserSettingsPage } from 'client/blocks/UserSettingsPage/UserSettingsPage';
import { reachGoal } from 'client/lib/metrika';
import { mapChannelToDefaultName } from 'client/utils/settings/alice-skill';
import { keepIfInEnum } from 'common/utils/keepIfInEnum';

import { createApp, createSkill, getAppsMeta, getSnapshot } from '../../api';
import * as AppsMetaInfoContext from '../../context/appsMetaInfoContext';
import * as SnapshotContext from '../../context/snapshotContext';
import { useInterval } from '../../hooks/useInterval';
import { useMessageSubscription } from '../../hooks/useMessageSubscription';
import { useSessionFlags } from '../../hooks/useSessionFlags';
import { routes } from '../../lib/routes';
import { Channel } from '../../model/skill';
import { InstallableAppsMeta, Snapshot } from '../../types';
import {
    canUseAliceApps,
    getIsYangoRequest,
    hrefIsExternal,
    isIframe,
    isInstallableAppChannel,
    showAppStoreUi,
    showAutoAppUi,
    showQuickAppsUi,
} from '../../utils';
import { tycoon } from '../../utils/messages';

import '@gravity-ui/uikit/styles/styles.css';

import './App.scss';

const { snapshotRefreshIntervalMs } = __appConfig.app;

const App: React.FC = () => {
    const [theme, setTheme] = useState<'light' | 'dark'>('light');

    useEffect(() => {
        const updateColorScheme = () => {
            if (window.matchMedia) {
                if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
                    setTheme('dark');
                } else {
                    setTheme('light');
                }
            }
        };
        updateColorScheme();
        const colorSchemeQuery = !!window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)');
        if (colorSchemeQuery) {
            colorSchemeQuery.addEventListener('change', updateColorScheme);
        }
        return () => {
            if (colorSchemeQuery) {
                colorSchemeQuery.removeEventListener('change', updateColorScheme);
            }
        };
    }, []);

    return (
        <ErrorBoundary withHomepageLink>
            <ThemeProvider theme={theme}>
                <ToasterProvider toaster={toaster}>
                    <Routes>
                        <Route path={routes.app.email} element={<EmailConfirmation />} />
                        <Route path={routes.app.unsubscribe} element={<Unsubscribe />} />
                        <Route path={routes.app.accountLinkingComplete} element={<AuthRedirect />} />
                        <Route path={`${routes.app.root}/*`} element={<DeveloperConsoleApp />} />
                        <Route path="*" element={<Navigate to={routes.app.root} replace />} />
                    </Routes>
                    <ToasterComponent />
                </ToasterProvider>
            </ThemeProvider>
        </ErrorBoundary>
    );
};

const SkillPageWithParams = () => {
    const { skillId } = useParams<{ skillId: string }>();
    return <SkillAppPageContainer skillId={skillId as string} />;
};

const InstallableAppPageWithParams = ({ channel }: { channel: Channel }) => {
    const { skillId } = useParams<{ skillId: string }>();
    return <InstallableAppPageContainer channel={channel} skillId={skillId as string} />;
};

const AliceAgentAppPageWithParams = () => {
    const { skillId } = useParams<{ skillId: string }>();
    return <AliceAgentAppPageContainer skillId={skillId as string} />;
};

const AliceExternalAppPageWithParams = () => {
    const { skillId } = useParams<{ skillId: string }>();
    return <AliceExternalAppPageContainer skillId={skillId as string} />;
};

const DeveloperConsoleApp: React.FC = () => {
    const navigate = useNavigate();
    const user = __appConfig.user;

    useTwoFaToast();

    useSessionFlags([
        '__display-smart-home-testing-page',
        '__display-cloud-functions',
        '__display-smart-home-cloud-functions',
        '__display-monitoring-page',
        '__display-smart-home-monitoring-page',
        '__display-donation-page',
        '__display-device-linking',
        '__display-app-metrica-settings',
        '__display-rsy-field',
        '__display-trusted-checkbox',
        '__display-anonymous-option',
        '__display-skill-access',
        '__display-sharing-page',
        '__display-testing-date-selector',
    ]);

    useMessageSubscription(async (data) => {
        switch (data.type) {
            case 'navigation':
                if (!hrefIsExternal.test(data.payload.path)) {
                    navigate(data.payload.path);
                }
                return;

            case 'sprav_dialogs_inited':
                tycoon.sendIframeInited();

                return;
            default:
                return;
        }
    });

    useEffect(() => {
        tycoon.sendIframeInited();
    }, []);

    if (!user) {
        const passportUrl = __appConfig.passport + '?retpath=' + encodeURIComponent(location.href);

        window.location.href = passportUrl;

        return (
            <div className="AppLoaderContainer">
                <PageLoader />
            </div>
        );
    }

    return (
        <>
            <Routes>
                <Route
                    path="settings/*"
                    element={
                        <SkillDataProvider>
                            <UserSettingsPage />
                        </SkillDataProvider>
                    }
                />
                <Route
                    path="moderation/skills/:skillId/draft/test"
                    element={
                        <SkillDataProvider>
                            <AliceSkillModerationTestPageWithParams />
                        </SkillDataProvider>
                    }
                />
                <Route
                    path="skills"
                    element={
                        <SkillDataProvider>
                            <SkillsIndexPage />
                        </SkillDataProvider>
                    }
                />
                <Route
                    path="smart-home"
                    element={
                        <SkillDataProvider>
                            <AppsIndex channel={Channel.SmartHome} />
                        </SkillDataProvider>
                    }
                />
                <Route
                    path="skills/:skillId/*"
                    element={
                        <SkillDataProvider>
                            <SkillPageWithParams />
                        </SkillDataProvider>
                    }
                />
                <Route
                    path="skills/:skillId"
                    element={
                        <SkillDataProvider>
                            <SkillPageWithParams />
                        </SkillDataProvider>
                    }
                />
                <Route
                    path="tv-apps"
                    element={
                        <InstallableAppDataProvider>
                            <AppsIndex channel={Channel.TvApp} />
                        </InstallableAppDataProvider>
                    }
                />
                <Route
                    path="tv-apps/:skillId/*"
                    element={
                        <InstallableAppDataProvider>
                            <InstallableAppPageWithParams channel={Channel.TvApp} />
                        </InstallableAppDataProvider>
                    }
                />
                <Route
                    path="auto-apps"
                    element={
                        <InstallableAppDataProvider>
                            {showAutoAppUi() ? (
                                <AppsIndex channel={Channel.AutoApp} />
                            ) : (
                                <Navigate to={routes.app.root} replace />
                            )}
                        </InstallableAppDataProvider>
                    }
                />
                <Route
                    path="auto-apps/:skillId/*"
                    element={
                        <InstallableAppDataProvider>
                            {showAutoAppUi() ? (
                                <InstallableAppPageWithParams channel={Channel.AutoApp} />
                            ) : (
                                <Navigate to={routes.app.root} replace />
                            )}
                        </InstallableAppDataProvider>
                    }
                />
                <Route
                    path="smart-screen"
                    element={
                        <InstallableAppDataProvider>
                            <AppsIndex channel={Channel.SmartScreenApp} />
                        </InstallableAppDataProvider>
                    }
                />
                <Route
                    path="smart-screen/:skillId/*"
                    element={
                        <InstallableAppDataProvider>
                            <InstallableAppPageWithParams channel={Channel.SmartScreenApp} />
                        </InstallableAppDataProvider>
                    }
                />
                <Route
                    path="all-available-apps/*"
                    element={
                        <InstallableAppDataProvider>
                            <AllAvailableAppsRouter />
                        </InstallableAppDataProvider>
                    }
                />
                <Route
                    path="alice-apps"
                    element={canUseAliceApps() ? <AliceAppsIndexPage /> : <Navigate to={routes.app.root} replace />}
                />
                <Route
                    path="alice-apps/internal/:skillId/*"
                    element={
                        canUseAliceApps() ? <AliceAgentAppPageWithParams /> : <Navigate to={routes.app.root} replace />
                    }
                />
                <Route
                    path="alice-apps/external/:skillId/*"
                    element={
                        canUseAliceApps() ? (
                            <AliceExternalAppPageWithParams />
                        ) : (
                            <Navigate to={routes.app.root} replace />
                        )
                    }
                />
                <Route
                    path="quick-apps/*"
                    element={
                        <InstallableAppDataProvider>
                            {showQuickAppsUi() ? <QuickApps /> : <Navigate to={routes.app.root} replace />}
                        </InstallableAppDataProvider>
                    }
                />
                <Route
                    index
                    element={
                        <SkillDataProvider>
                            <SkillsIndexPage />
                        </SkillDataProvider>
                    }
                />
                <Route path="*" element={<Navigate to={routes.app.root} replace />} />
            </Routes>
        </>
    );
};

const AliceSkillModerationTestPageWithParams = () => {
    const params = useParams<{ skillId: string }>();
    return <AliceSkillModerationTestPage skillId={params.skillId as string} />;
};

const SkillsIndexPage = () => {
    if (isIframe()) return null;
    return getIsYangoRequest() ? <AppsIndex channel={Channel.SmartHome} /> : <AppsIndex channel={Channel.AliceSkill} />;
};

const SkillDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const navigate = useNavigate();
    const [snapshot, setSnapshot] = useState<Snapshot | undefined>();

    const fetchSkills = useCallback(async () => {
        try {
            setSnapshot(await getSnapshot());
        } catch (err) {
            console.error(err);
        }
    }, []);

    useEffect(() => {
        const setup = async () => {
            try {
                setSnapshot(await getSnapshot());
            } catch (err) {
                console.error(err);
            }
        };
        setup();
    }, [fetchSkills]);

    useInterval(fetchSkills, snapshotRefreshIntervalMs);

    const createNewSkill = useCallback(
        async (channelValue: string | undefined) => {
            const channel = keepIfInEnum(channelValue, Channel);
            if (!channel || isInstallableAppChannel(channel)) {
                console.error('@App wrong channel type ', channelValue);
                return;
            }
            const skill =
                channel === Channel.Thereminvox
                    ? await createSkill(channel)
                    : await createApp(mapChannelToDefaultName[channel], channel);
            await fetchSkills();

            navigate(routes.app.skillPage(true, 'settings', skill.id));
            reachGoal('createSkill');
        },
        [fetchSkills, navigate],
    );

    if (!snapshot) {
        return (
            <div className="AppLoaderContainer">
                <PageLoader />
            </div>
        );
    }

    return (
        <SnapshotContext.Provider value={{ snapshot, refreshSnapshot: fetchSkills, createSkill: createNewSkill }}>
            {children}
        </SnapshotContext.Provider>
    );
};

const InstallableAppDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const navigate = useNavigate();
    const [appsMetaInfo, setAppsMetaInfo] = useState<InstallableAppsMeta | undefined>();

    const fetchApps = useCallback(async () => {
        if (!showAppStoreUi()) {
            return;
        }

        try {
            const res = await getAppsMeta();
            setAppsMetaInfo(res.installable_apps);
        } catch (err) {
            console.error(err);
        }
    }, []);

    useEffect(() => {
        fetchApps();
    }, [fetchApps]);

    useInterval(fetchApps, snapshotRefreshIntervalMs);

    const handleAppCreation = useCallback(
        async (channel: Channel) => {
            if (isInstallableAppChannel(channel)) {
                const { skillId } = await createApp(mapChannelToDefaultName[channel], channel);
                await fetchApps();
                navigate(routes.app.appPage(channel, true, 'settings', skillId));
            } else {
                const skill =
                    channel === Channel.Thereminvox
                        ? await createSkill(channel)
                        : await createApp(mapChannelToDefaultName[channel], channel);
                await fetchApps();
                navigate(routes.app.skillPage(true, 'settings', skill.id));
            }
        },
        [fetchApps, navigate],
    );

    return (
        <AppsMetaInfoContext.Provider
            value={{
                appsMetaInfo: appsMetaInfo ?? {
                    maxAppsPerUser: 0,
                    targetPlatformsByChannel: {},
                    categories: [],
                    ageRestrictions: [],
                },
                handleAppCreation,
            }}
        >
            {children}
        </AppsMetaInfoContext.Provider>
    );
};

const AppWithLang = () => (
    <Lang.Provider value={{ lang: __appConfig.lang }}>
        <App />
    </Lang.Provider>
);

const RoutedApp: React.FC = () => {
    const router = useMemo(() => createBrowserRouter([{ path: '*', Component: AppWithLang }]), []);

    return <RouterProvider router={router} />;
};

export { RoutedApp as App };
